import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Collapse,
  DatePicker,
  Drawer,
  Empty,
  Input,
  Popconfirm,
  Select,
  Tag,
} from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  archiveReviewZone,
  errorMessage,
  fetchReviewZones,
  fetchReviewZone,
  saveReviewZone,
} from './api';
import { Instrument, Period, ReviewNews, ReviewZone } from './types';
import { periodDates } from './format';

type Props = {
  instrument: Instrument;
  runId: string;
  period: Period | null;
  selectedNews: ReviewNews[];
  disabled: boolean;
  onRestore: (zone: ReviewZone) => void;
  onSaved: (zone: ReviewZone) => void;
  activeZone?: ReviewZone | null;
  onArchived?: (id: string) => void;
};

const ReviewZones: React.FC<Props> = ({
  instrument,
  runId,
  period,
  selectedNews,
  disabled,
  onRestore,
  onSaved,
  activeZone,
  onArchived,
}) => {
  const draftKey = useRef<string | null>(null);
  const [nextPage, setNextPage] = useState<number | null>(null);
  const [total, setTotal] = useState(0);
  const [zones, setZones] = useState<ReviewZone[]>([]);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState<ReviewZone | null>(null);
  const [title, setTitle] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [notes, setNotes] = useState('');
  const [labels, setLabels] = useState<string[]>([]);
  const [status, setStatus] = useState<ReviewZone['status']>('draft');
  const [links, setLinks] = useState<ReviewNews[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [latest, setLatest] = useState<ReviewZone | null>(null);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    setZones([]);
    fetchReviewZones(instrument.id, controller.signal)
      .then(page => {
        if (!controller.signal.aborted) {
          setZones(page.results);
          setNextPage(page.next_page);
          setTotal(page.count ?? page.results.length);
        }
      })
      .catch(reason => {
        if (!controller.signal.aborted) setError(errorMessage(reason));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [instrument.id, refresh]);

  const edit = (zone: ReviewZone | null, force = false) => {
    if (!zone && !period) return;
    const key = zone?.id || `${runId}:${period?.id}`;
    if (!force && draftKey.current === key) {
      setOpen(true);
      return;
    }
    draftKey.current = key;
    setCurrent(zone);
    setTitle(zone?.title || period?.title || '');
    setStart(
      zone?.start_date ||
        period?.display_window?.start_date ||
        period?.start_date ||
        '',
    );
    setEnd(
      zone?.end_date ||
        period?.display_window?.end_date ||
        period?.end_date ||
        '',
    );
    setNotes(zone?.notes || '');
    setLabels(zone?.labels || []);
    setStatus(zone?.status || 'draft');
    setLinks(zone?.news || selectedNews);
    setConflict(false);
    setLatest(null);
    setError('');
    setOpen(true);
  };
  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...(current
          ? { expected_revision: current.revision }
          : { run_id: runId, period_id: period?.id }),
        title: title.trim(),
        start_date: start,
        end_date: end,
        notes,
        labels,
        status,
        ...(!current || JSON.stringify(links) !== JSON.stringify(current.news)
          ? {
              news: links.map(link => ({
                news_id: link.news_id,
                labels: link.labels,
                judgment: link.judgment,
                ...(link.curation_revision
                  ? { curation_revision: link.curation_revision }
                  : {}),
                ...(link.evidence_job_id
                  ? { evidence_job_id: link.evidence_job_id }
                  : {}),
              })),
            }
          : {}),
      };
      const saved = await saveReviewZone(payload, current?.id);
      draftKey.current = null;
      setOpen(false);
      setRefresh(value => value + 1);
      onSaved(saved);
    } catch (reason) {
      setError(errorMessage(reason));
      setConflict((reason as { status?: number }).status === 409);
    } finally {
      setSaving(false);
    }
  };
  const mergeSelected = () => {
    setLinks(currentLinks => {
      const merged = new Map(
        currentLinks.map(link => [String(link.news_id), link]),
      );
      selectedNews.forEach(link => {
        const existing = merged.get(String(link.news_id));
        merged.set(String(link.news_id), {
          ...link,
          ...(existing
            ? { labels: existing.labels, judgment: existing.judgment }
            : {}),
        });
      });
      return Array.from(merged.values());
    });
    if (/article|evidence job/i.test(error)) {
      setConflict(false);
      setError('');
    }
  };
  const more = async () => {
    if (!nextPage) return;
    setLoading(true);
    try {
      const page = await fetchReviewZones(instrument.id, undefined, nextPage);
      setZones(current => [...current, ...page.results]);
      setNextPage(page.next_page);
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setLoading(false);
    }
  };
  const reviewLatest = async () => {
    if (!current) return;
    setLoading(true);
    try {
      const zone = await fetchReviewZone(current.id);
      setLatest(zone);
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setLoading(false);
    }
  };
  const archive = async (zone: ReviewZone) => {
    setSaving(true);
    setError('');
    try {
      await archiveReviewZone(zone.id, zone.revision);
      if (draftKey.current === zone.id) draftKey.current = null;
      onArchived?.(zone.id);
      setRefresh(value => value + 1);
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setSaving(false);
    }
  };
  return (
    <section
      className="market-explorer__review-zones"
      aria-label="Saved review zones"
    >
      <div className="market-explorer__review-zone-heading">
        <span>
          <strong>Review workspace</strong>
          {activeZone && (
            <small>
              Saved zone: {activeZone.title} ·{' '}
              {periodDates(activeZone.start_date, activeZone.end_date)}
            </small>
          )}
          <small>
            {selectedNews.length
              ? `${selectedNews.length} articles selected for this zone`
              : 'Save intervals, context and your evidence selections.'}
          </small>
        </span>
        <Button
          icon={<SaveOutlined />}
          disabled={!period || disabled}
          onClick={() => edit(activeZone || null)}
        >
          {activeZone ? 'Update zone' : 'Save zone'}
        </Button>
      </div>
      {error && !open && (
        <Alert
          type="warning"
          showIcon
          title="Review zones unavailable"
          description={error}
          action={
            <Button size="small" onClick={() => setRefresh(value => value + 1)}>
              Retry
            </Button>
          }
        />
      )}
      <Collapse
        ghost
        size="small"
        items={[
          {
            key: 'zones',
            label: `Saved zones · ${total}${loading ? ' · loading' : ''}`,
            children: (
              <div className="market-explorer__saved-zone-list">
                {!loading && !zones.length && (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No saved zones for this instrument yet."
                  />
                )}
                {zones.map(zone => (
                  <div key={zone.id}>
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => onRestore(zone)}
                    >
                      <strong>{zone.title}</strong>
                      <span>
                        {periodDates(zone.start_date, zone.end_date)} ·{' '}
                        {zone.news.length} articles
                      </span>
                    </button>
                    <Tag>{zone.status}</Tag>
                    <Button
                      type="text"
                      size="small"
                      disabled={disabled}
                      onClick={() => edit(zone)}
                    >
                      Edit
                    </Button>
                    <Popconfirm
                      title="Archive this review zone?"
                      description="The analysis and original news will remain available."
                      onConfirm={() => archive(zone)}
                    >
                      <Button
                        type="text"
                        size="small"
                        disabled={disabled || saving}
                      >
                        Archive
                      </Button>
                    </Popconfirm>
                  </div>
                ))}
                {nextPage && (
                  <Button type="link" loading={loading} onClick={more}>
                    Show more saved zones
                  </Button>
                )}
              </div>
            ),
          },
        ]}
      />
      <Drawer
        open={open}
        title={current ? 'Edit review zone' : 'Save a review zone'}
        onClose={() => !saving && setOpen(false)}
        size="large"
        extra={
          <Button
            type="primary"
            loading={saving}
            disabled={
              !title.trim() || !start || !end || start > end || conflict
            }
            onClick={save}
          >
            Save zone
          </Button>
        }
      >
        <div className="market-explorer__zone-form">
          <p>
            Your notes and labels describe this zone. Original price detections
            and article text are preserved.
          </p>
          <label>
            Title
            <Input
              value={title}
              maxLength={200}
              onChange={event => setTitle(event.target.value)}
            />
          </label>
          <label>
            Zone dates
            <DatePicker.RangePicker
              value={[dayjs(start), dayjs(end)]}
              allowClear={false}
              onChange={values => {
                if (values?.[0] && values?.[1]) {
                  setStart(values[0].format('YYYY-MM-DD'));
                  setEnd(values[1].format('YYYY-MM-DD'));
                }
              }}
            />
          </label>
          <label>
            Review status
            <Select
              value={status}
              onChange={setStatus}
              options={['draft', 'reviewed', 'rejected'].map(value => ({
                value,
                label: value.charAt(0).toUpperCase() + value.slice(1),
              }))}
            />
          </label>
          <label>
            Context labels
            <Select
              mode="tags"
              value={labels}
              onChange={setLabels}
              placeholder="Type a label and press Enter"
              tokenSeparators={[',']}
            />
          </label>
          <label>
            Reviewer notes
            <Input.TextArea
              value={notes}
              onChange={event => setNotes(event.target.value)}
              autoSize={{ minRows: 4, maxRows: 12 }}
            />
          </label>
          <h3>Selected evidence · {links.length}</h3>
          {selectedNews.length > 0 && (
            <Button onClick={mergeSelected}>
              Use {selectedNews.length} selected articles
            </Button>
          )}
          {!links.length && (
            <p>
              Select articles using “Keep for zone” beside the news rows, then
              save a zone.
            </p>
          )}
          {links.map((link, index) => (
            <div className="market-explorer__zone-news" key={link.news_id}>
              <strong>{link.headline || `Article ${link.news_id}`}</strong>
              <Select
                aria-label={`Evidence judgment for ${link.headline || link.news_id}`}
                value={link.judgment}
                options={[
                  { value: 'unreviewed', label: 'Unreviewed' },
                  { value: 'relevant', label: 'Relevant' },
                  { value: 'not_relevant', label: 'Not relevant' },
                ]}
                onChange={judgment =>
                  setLinks(values =>
                    values.map((value, i) =>
                      i === index ? { ...value, judgment } : value,
                    ),
                  )
                }
              />
              <Select
                mode="tags"
                aria-label={`Context labels for ${link.headline || link.news_id}`}
                placeholder="Labels for this article in this zone"
                value={link.labels}
                onChange={value =>
                  setLinks(values =>
                    values.map((item, i) =>
                      i === index ? { ...item, labels: value } : item,
                    ),
                  )
                }
              />
              <Button
                type="link"
                size="small"
                onClick={() =>
                  setLinks(values => values.filter((_, i) => i !== index))
                }
              >
                Remove from zone
              </Button>
            </div>
          ))}
          {error && (
            <Alert
              type="warning"
              showIcon
              title={
                conflict ? 'This zone changed elsewhere' : 'Zone not saved'
              }
              description={
                <>
                  <p>{error}</p>
                  {conflict &&
                    (/article|evidence job/i.test(error) ? (
                      <p>
                        Your draft is kept. Close this drawer, refresh the news
                        source, select the updated articles, then reopen this
                        zone and choose “Use selected articles”.
                      </p>
                    ) : (
                      <>
                        <p>
                          Your draft is kept. Review the latest saved zone
                          before retrying.
                        </p>
                        <Button
                          size="small"
                          loading={loading}
                          onClick={reviewLatest}
                        >
                          Review latest saved zone
                        </Button>
                      </>
                    ))}
                </>
              }
            />
          )}
          {latest && (
            <div className="market-explorer__zone-news">
              <h4>Latest saved zone</h4>
              <strong>{latest.title}</strong>
              <p>
                {periodDates(latest.start_date, latest.end_date)} ·{' '}
                {latest.status}
              </p>
              <p>{latest.notes || 'No reviewer notes.'}</p>
              <p>
                {latest.news.length} selected articles ·{' '}
                {(latest.labels || []).join(', ') || 'No context labels'}
              </p>
              <Button
                onClick={() => {
                  setCurrent(latest);
                  setLatest(null);
                  setConflict(false);
                  setError('');
                }}
              >
                Keep my draft with the latest revision
              </Button>
              <Button type="link" onClick={() => edit(latest, true)}>
                Use latest saved zone
              </Button>
            </div>
          )}
        </div>
      </Drawer>
    </section>
  );
};

export default ReviewZones;
